import type { QuestionDetail } from "@models/Dataset";
import type {
  MetricBase,
  MetricPopulation,
  MetricResultType,
  MetricSource,
  MetricSourceQuestionRef,
} from "@models/Metric";

/**
 * The search/list endpoint returns abbreviated type codes ("MULTI", "NUMERIC");
 * the detail endpoint returns the full word ("MULTIPLE", "NUMBER"). Normalize
 * both. Duplicated from scorecard-utils.ts rather than imported — Metrics is a
 * deliberately parallel, non-shared implementation from the chart-scoped
 * rollup/focus/shape-classification code.
 */
export function normalizeQuestionType(type: string | null | undefined): string {
  const upper = type?.toUpperCase() ?? "";
  if (upper === "MULTI") return "MULTIPLE";
  if (upper === "NUMERIC") return "NUMBER";
  return upper;
}

export type MetricQuestionShape =
  | "single_one_dimensional"
  | "multiple_one_dimensional"
  | "single_grid"
  | "numeric";

/**
 * Single grid is not a distinct API question type — it's a "Single" question
 * with >=2 row variables (mirrors classifyBaseQuestionShape's grid rule).
 * Always classify off a fully-loaded QuestionDetail (full-word `type`,
 * `rowVariables`), never off the abbreviated search/list response — that
 * response's ability to signal Number/Float is unconfirmed, so the source
 * question selector re-fetches QuestionDetail on selection before
 * classifying, the same sidestep MetricRowBuilder.tsx uses for Scorecard.
 */
export function classifyMetricQuestionShape(
  type: string | null | undefined,
  rowVariableCount: number,
): MetricQuestionShape | null {
  const normalized = normalizeQuestionType(type);
  if (normalized === "SINGLE") {
    return rowVariableCount >= 2 ? "single_grid" : "single_one_dimensional";
  }
  if (normalized === "MULTIPLE") return "multiple_one_dimensional";
  if (normalized === "NUMBER" || normalized === "FLOAT") return "numeric";
  return null;
}

export function getMetricResultTypeForShape(
  shape: MetricQuestionShape,
): MetricResultType {
  return shape === "numeric" ? "mean" : "percentage";
}

function toQuestionRef(question: QuestionDetail): MetricSourceQuestionRef {
  return {
    questionId: question.datasetElementId,
    questionLabel: question.label || question.name,
    questionTitle: question.title || question.name,
  };
}

/**
 * Fresh default MetricSource for a newly-selected question, per PRD defaults:
 * one-dimensional Single defaults to all source categories; grids default to
 * Value Focus with no value chosen yet (invalid until the researcher picks
 * one, which simply keeps Save disabled — not an error state).
 */
export function buildDefaultMetricSourceForShape(
  shape: MetricQuestionShape,
  question: QuestionDetail,
): MetricSource {
  const questionRef = toQuestionRef(question);
  switch (shape) {
    case "single_one_dimensional":
      return {
        kind: "single_one_dimensional",
        resultType: "percentage",
        question: questionRef,
        focus: { mode: "all" },
      };
    case "multiple_one_dimensional":
      return {
        kind: "multiple_one_dimensional",
        resultType: "percentage",
        question: questionRef,
        focus: { variableIds: [], variableLabels: {} },
      };
    case "single_grid":
      return {
        kind: "single_grid_value_focus",
        resultType: "percentage",
        question: questionRef,
        focus: { mode: "one", valueId: "", valueLabel: "" },
      };
    case "numeric": {
      const variableLabels: Record<string, string> = {};
      question.rowVariables.forEach((v) => {
        variableLabels[v.id] = v.rowtitle || v.title;
      });
      return {
        kind: "numeric",
        resultType: "mean",
        question: questionRef,
        focus: {
          variableIds: question.rowVariables.map((v) => v.id),
          variableLabels,
        },
      };
    }
  }
}

export const METRIC_QUESTION_SHAPE_LABEL: Record<MetricQuestionShape, string> = {
  single_one_dimensional: "Single",
  multiple_one_dimensional: "Multiple",
  single_grid: "Single grid",
  numeric: "Number/Float",
};

export function getMetricResultTypeLabel(resultType: MetricResultType): string {
  return resultType === "mean" ? "Mean" : "Percentage";
}

export function formatMetricPopulationLabel(population: MetricPopulation): string {
  return population.mode === "total_survey_population"
    ? "Total survey population"
    : "Valid question respondents";
}

function isMetricRollupValid(rollup: { name: string; sourceIds: string[] }): boolean {
  return !!rollup.name.trim() && rollup.sourceIds.length >= 2;
}

/** Each condition can only enter a metric's source via the builder's own Save
 *  action, which gates on this — so a persisted source is guaranteed valid by
 *  construction, but this must still hold at Save time in the editor. */
export function isMetricSourceValid(source: MetricSource | null): boolean {
  if (!source || !source.question.questionId) return false;
  switch (source.kind) {
    case "single_one_dimensional":
      if (source.focus.mode === "all") return true;
      if (source.focus.mode === "one") return !!source.focus.valueId;
      return isMetricRollupValid(source.focus.rollup);
    case "multiple_one_dimensional":
      return source.focus.variableIds.length > 0;
    case "single_grid_value_focus":
      return source.focus.mode === "one"
        ? !!source.focus.valueId
        : isMetricRollupValid(source.focus.rollup);
    case "single_grid_variable_focus":
      return !!source.focus.variableId;
    case "numeric":
      return source.focus.variableIds.length > 0;
  }
}

export function isMetricBaseValid(base: MetricBase): boolean {
  if (base.mode === "all_eligible") return true;
  if (base.mode === "custom") return !!base.name.trim() && base.conditions.length > 0;
  return !!base.preconfiguredBaseId;
}

/** Compact, catalog-friendly description of a metric's focus/rollup selection. */
export function describeMetricFocus(source: MetricSource): string {
  switch (source.kind) {
    case "single_one_dimensional":
      if (source.focus.mode === "all") return "All source categories";
      if (source.focus.mode === "one") return source.focus.valueLabel;
      return `Rollup: ${source.focus.rollup.name}`;
    case "multiple_one_dimensional":
      return `${source.focus.variableIds.length} selected variable${source.focus.variableIds.length === 1 ? "" : "s"}`;
    case "single_grid_value_focus":
      return source.focus.mode === "one"
        ? `Value Focus: ${source.focus.valueLabel}`
        : `Value Focus rollup: ${source.focus.rollup.name}`;
    case "single_grid_variable_focus":
      return `Variable Focus: ${source.focus.variableLabel}`;
    case "numeric":
      return `All numeric variables (${source.focus.variableIds.length})`;
  }
}

/** Hover-tooltip explanation of *why* a metric's focus/rollup selection is
 *  flagged as using hidden content — shown when describeMetricFocus's own
 *  text is rendered in the hidden-content warning color. Mirrors
 *  describeMetricFocus's per-kind switch so the wording always matches what's
 *  actually selected (a rollup's members vs. a single picked value/variable
 *  vs. a dynamically-enumerated set), written as a full sentence for the
 *  tooltip rather than a compact label. */
export function describeMetricHiddenContentDetail(source: MetricSource): string {
  switch (source.kind) {
    case "single_one_dimensional":
      if (source.focus.mode === "rollup")
        return "One or more values combined in this rollup are hidden in the dataset.";
      if (source.focus.mode === "one")
        return "The selected value is hidden in the dataset.";
      return "One or more categories of this question are hidden in the dataset.";
    case "multiple_one_dimensional":
      return "The selected variable is hidden in the dataset.";
    case "single_grid_value_focus":
      return source.focus.mode === "rollup"
        ? "One or more values combined in this rollup are hidden in the dataset."
        : "The selected value is hidden in the dataset.";
    case "single_grid_variable_focus":
      return "The selected variable is hidden in the dataset.";
    case "numeric":
      return "The selected variable is hidden in the dataset.";
  }
}

function resolveValueLabels(
  sourceIds: string[],
  questionDetail: QuestionDetail | null,
): string | null {
  const labels = sourceIds
    .map((id) => questionDetail?.values.find((v) => v.id === id))
    .filter((v): v is NonNullable<typeof v> => !!v)
    .map((v) => v.title || v.label);
  return labels.length > 0 ? labels.join(", ") : null;
}

/**
 * Full "Saved definition" selection description for a metric — a short
 * label (e.g. "Value Focus · Quality T3B") plus, for a rollup or a Multiple/
 * Number variable set, the member category/variable names it combines (e.g.
 * "Average, Good, Excellent") as a separate `details` line. Mirrors PRD-66's
 * creation-flow mockup's "Selection" row; `describeMetricFocus` above stays
 * the compact one-line version used in catalog rows. `questionDetail` is
 * only needed to resolve rollup member labels (see
 * bar-chart-metric-utils.ts's barChartMetricNeedsQuestionDetailFetch) — null
 * safely yields no `details` line rather than throwing.
 */
export function describeMetricSelection(
  source: MetricSource,
  questionDetail: QuestionDetail | null,
): { label: string; details: string | null } {
  switch (source.kind) {
    case "single_one_dimensional":
      if (source.focus.mode === "all") return { label: "All source categories", details: null };
      if (source.focus.mode === "one") return { label: source.focus.valueLabel, details: null };
      return {
        label: `Rollup · ${source.focus.rollup.name}`,
        details: resolveValueLabels(source.focus.rollup.sourceIds, questionDetail),
      };
    case "multiple_one_dimensional": {
      const ids = source.focus.variableIds;
      const labels = ids.map((id) => source.focus.variableLabels[id] ?? id);
      return {
        label: `${ids.length} selected response variable${ids.length === 1 ? "" : "s"}`,
        details: labels.length > 0 ? labels.join(", ") : null,
      };
    }
    case "single_grid_value_focus":
      if (source.focus.mode === "one") {
        return { label: `Value Focus · ${source.focus.valueLabel}`, details: null };
      }
      return {
        label: `Value Focus · ${source.focus.rollup.name}`,
        details: resolveValueLabels(source.focus.rollup.sourceIds, questionDetail),
      };
    case "single_grid_variable_focus":
      return { label: `Variable Focus · ${source.focus.variableLabel}`, details: null };
    case "numeric": {
      const ids = source.focus.variableIds;
      const labels = ids.map((id) => source.focus.variableLabels[id] ?? id);
      return {
        label: `All numeric variables (${ids.length})`,
        details: labels.length > 0 ? labels.join(", ") : null,
      };
    }
  }
}
